// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). Header used across the app.
import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { MenuItem, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { catchError, EMPTY, filter, map, switchMap } from 'rxjs';
import { ButtonLabels } from 'src/app/core/common/enums/select-solution.enum';
import { DialogCommonService } from 'src/app/core/services/common/dialog-common.service';
import { DiagnosisSessionService } from 'src/app/core/services/diagnosis-session.service';
import { StoreService } from 'src/app/core/services/global-store/store.service';
import { setIsDesignApproved } from 'src/app/core/store/actions/required-action.actions';
import { navigateToNextStep } from 'src/app/core/store/actions/router.action';
import {
  setChallenges,
  setScreenStates,
} from 'src/app/core/store/actions/select-challenge.actions';
import {
  setAvailableSolutions,
  setIsAvailableSolutionSelected,
  setIsSelectSolutionFlag,
} from 'src/app/core/store/actions/select-solution.action';
import { ChallengesSubChallengesStore } from 'src/app/views/select-challenge/components/select-challenges-container/challenges-sub-challenges/challenges-sub-challenges.store';
import { AvailableSolutionStore } from '../../../../views/select-solutions/components/select-solutions-container/available-solutions/available-solutions.store';
import { RoleService } from '../../../admin-features/services/authorization/role.service';
import { LOCAL_VARS, PARAMS, ROUTES } from '../../constants/routes.constant';
import {
  CURRENT_STATUS,
  STATUS_CLASS_MAPPING,
} from '../../constants/status.constant';
import { PSL } from '../../models/master-data/psl';
import { DiagnosisSession } from '../../models/workflow/diagnosis-session';

@Component({
  selector: 'app-header',
  imports: [
    AvatarModule,
    AvatarGroupModule,
    ButtonModule,
    NgClass,
    Menu,
    TagModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  readonly #globalStore = inject(Store);
  readonly challengeStore = inject(ChallengesSubChallengesStore);
  readonly availableSolutionStore = inject(AvailableSolutionStore);
  readonly #destroyRef = inject(DestroyRef);
  readonly #authService = inject(MsalService);
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #storeService = inject(StoreService);
  readonly #diagnosisSessionService = inject(DiagnosisSessionService);
  dialogCommonService = inject(DialogCommonService);
  #roleService = inject(RoleService);
  toastr = inject(MessageService);

  @Input() diagnosisSessionId: string = '';
  @Input() externalId: string = '';
  @Input() isVDR: boolean = false;
  userName = '';
  labelAvatar = '';
  blockingTime = 10;
  buttonLabel = ButtonLabels;
  ROUTES = ROUTES;
  status = CURRENT_STATUS;
  STATUS_CLASS_MAPPING = STATUS_CLASS_MAPPING;

  currentStep = signal(ROUTES.SELECT_CHALLENGES);
  nextStepButtonLabel = {
    [ROUTES.SELECT_CHALLENGES]: this.buttonLabel.InitializeDiagnosis,
    [ROUTES.SELECT_SOLUTIONS]: this.buttonLabel.ViewSummary,
    [ROUTES.SOLUTION_SELECTION]: this.buttonLabel.SolutionSelectionCompleted,
  };
  isDisableInitializeBtn = signal(false);
  isDisableViewSummaryBtn = toSignal(
    this.#storeService.getSelectedSolution().pipe(map((resp) => !resp)),
  );
  isDisableApprovedForDesignBtn = signal(false);

  diagnosisSessionStatus: string = '';
  diagnosisSession?: DiagnosisSession;
  hasRoleAdmin = toSignal(
    this.#roleService
      .getAssignedRoles()
      .pipe(map((res) => res.some((assignedRole) => assignedRole.isAdmin))),
  );

  items: Signal<MenuItem[]> = computed(() => [
    {
      label: this.challengeStore.selectedPSL().id
        ? `Selected PSL: ${this.challengeStore.selectedPSL().name}`
        : 'PSL Selection',
      disabled: this.currentStep() !== ROUTES.SELECT_CHALLENGES,
      command: () => {
        this.openPSLSelection();
      },
    },
    {
      label: 'Admin Page',
      styleClass: this.hasRoleAdmin() ? '' : 'hidden',
      command: () => {
        this.#router.navigate(['/admin']);
      },
    },
    {
      label: 'UoM Settings',
      command: () => {
        this.openUserSetting();
      },
    },
    {
      label: 'Log out',
      styleClass: 'log-out',
      command: () => {
        this.logOut();
      },
    },
  ]);

  nextBtnDisabled = computed(() => {
    switch (this.currentStep()) {
      case ROUTES.SELECT_CHALLENGES:
        return !this.challengeStore.pairChallenges().length;
      case ROUTES.SELECT_SOLUTIONS:
        return this.isDisableViewSummaryBtn();
      case ROUTES.SOLUTION_SELECTION:
        return (
          this.diagnosisSessionStatus === CURRENT_STATUS.SOLUTION_SELECTED ||
          this.diagnosisSessionStatus === CURRENT_STATUS.APPROVED_FOR_DESIGN
        );
      default:
        return false;
    }
  });

  ngOnInit(): void {
    this.processRouteActions();
    this.getActiveAccountInfo();
    this.verifySessionRequest();
  }

  verifySessionRequest() {
    if (this.externalId) {
      this.#diagnosisSessionService
        .getDiagnosisSessionByExternalId(this.externalId)
        .pipe(
          catchError(() => {
            this.resetDiagnosisStatus();
            return EMPTY;
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe((diagnosisSession) => {
          this.updateSessionData(diagnosisSession);
        });
    }

    if (this.diagnosisSessionId) {
      this.loadDiagnosisSession(this.diagnosisSessionId);
    }
  }

  loadDiagnosisSession(diagnosisSessionId: string) {
    this.#diagnosisSessionService
      .getDiagnosisSessionById(diagnosisSessionId)
      .subscribe({
        next: (diagnosisSession) => {
          this.updateSessionData(diagnosisSession);
        },
        error: () => {
          this.resetDiagnosisStatus();
        },
      });
  }

  updateSessionData(session: DiagnosisSession) {
    if (session) {
      this.checkSessionValid(session);
      this.diagnosisSession = session;
      this.diagnosisSessionId = session.id || '';
      this.diagnosisSessionStatus =
        session.diagnosisStatusName || session.diagnosisStatus.name;
      this.gotoPage(this.diagnosisSessionStatus);
      this.availableSolutionStore.disabledAllSolution(
        this.diagnosisSessionStatus === CURRENT_STATUS.SOLUTION_SELECTED ||
          this.diagnosisSessionStatus === CURRENT_STATUS.APPROVED_FOR_DESIGN,
      );
    } else {
      this.resetDiagnosisStatus();
    }
  }

  checkSessionValid(session?: DiagnosisSession) {
    if (!session) return;

    if (this.isSessionBlocking(session, this.currentStep())) {
      this.openBlockingWarning(
        `This session has been blocked by [${session.modifiedBy}]. Try again later.`,
      );
    } else if (this.isSessionCancelled(session)) {
      this.resetDiagnosisStatus();

      this.dialogCommonService.show(true, {
        title: 'Cancelled Session',
        message: `This session has been cancelled. Please contact admin for more information.`,
        closeLabel: 'Close',
        closable: false,
      });
    } else if (this.isSessionRestarted(session)) {
      this.resetDiagnosisStatus();

      this.dialogCommonService.show(true, {
        title: 'Restarted Session',
        message: `This session has been reset. You cannot access it anymore.`,
        closeLabel: 'Close',
        closable: false,
      });
    }
  }

  isSessionBlocking(session: DiagnosisSession, stepName: string): boolean {
    if (
      (stepName === ROUTES.SELECT_SOLUTIONS ||
        session.diagnosisStatusName === CURRENT_STATUS.IN_PROGRESS) &&
      this.isBlockingTime(session)
    )
      return true;

    return false;
  }

  isBlockingTime(session: DiagnosisSession): boolean {
    const lastModified = new Date(session.modifiedAt).getTime();
    const expirationTime = lastModified + this.blockingTime * 60000;
    const currentTime = this.getCurrentUTCDate().getTime();

    return (
      this.userName !== '' &&
      this.userName.toLowerCase() !== session.modifiedBy.toLowerCase() &&
      session.isBlocking &&
      expirationTime > currentTime
    );
  }

  private getCurrentUTCDate() {
    const current = new Date();
    return new Date(current.getTime() + current.getTimezoneOffset() * 60000);
  }

  isSessionCancelled(session: DiagnosisSession) {
    return session.diagnosisStatus.name === CURRENT_STATUS.CANCELLED;
  }

  isSessionRestarted(session: DiagnosisSession) {
    return session.diagnosisStatus.name === CURRENT_STATUS.RESTARTED;
  }

  resetDiagnosisStatus(): void {
    this.diagnosisSession = undefined;
    this.diagnosisSessionId = '';
    this.diagnosisSessionStatus = '';
    this.currentStep.set(ROUTES.SELECT_CHALLENGES);
  }

  getActiveAccountInfo(): void {
    const activeAccount = this.#authService.instance.getActiveAccount();

    this.userName = activeAccount?.name ? activeAccount?.name : '';
    //Get first letter from user name
    this.labelAvatar = this.userName
      .split(' ')
      .map((item) => item[0])
      .join('')
      .substring(0, 2);
  }

  logOut(): void {
    this.#authService.logoutRedirect();
  }

  processRouteActions(): void {
    const initializeDiagnosisScreen = () => {
      this.currentStep.set(ROUTES.SELECT_CHALLENGES);
      this.isDisableInitializeBtn.set(true);
    };
    const routeActions = {
      [ROUTES.SELECT_CHALLENGES]: initializeDiagnosisScreen,
      [ROUTES.SELECT_SOLUTIONS]: () =>
        this.currentStep.set(ROUTES.SELECT_SOLUTIONS),
      [ROUTES.VIEW_SUMMARY]: () => {
        this.currentStep.set(ROUTES.VIEW_SUMMARY);
        this.isDisableInitializeBtn.set(false);
      },
    };

    const routeKey = Object.values(ROUTES).find((route) =>
      this.#router.url.includes(route),
    );
    if (routeKey) {
      routeActions[routeKey]();
      this.currentStep.set(routeKey);
      if (this.currentStep() === ROUTES.SELECT_CHALLENGES)
        this.dispatchScreenState();
    }
  }

  handleRouterNavigate(): void {
    const dispatchNavigate = (
      pageRoute: string,
      sessionId: string,
      externalId?: string,
    ) => {
      this.#globalStore.dispatch(
        navigateToNextStep({
          pageRoute,
          sessionId,
          externalId,
        }),
      );
    };
    dispatchNavigate(this.currentStep(), this.diagnosisSessionId);
  }

  navigateToNextScreen(): void {
    if (this.currentStep() === ROUTES.SELECT_CHALLENGES) {
      this.initializeSession();
    } else if (this.currentStep() === ROUTES.SELECT_SOLUTIONS) {
      this.currentStep.set(ROUTES.VIEW_SUMMARY);
      this.handleRouterNavigate();
    }
  }

  backToPreviousScreen(): void {
    this.currentStep.update((currStep) =>
      currStep === ROUTES.VIEW_SUMMARY ? ROUTES.SELECT_SOLUTIONS : '',
    );

    this.logBackAction();

    this.handleRouterNavigate();
  }

  logBackAction() {
    if (
      this.currentStep() === ROUTES.SELECT_SOLUTIONS &&
      this.diagnosisSession?.diagnosisStatusName ===
        this.status.DIAGNOSIS_COMPLETED
    ) {
      this.#diagnosisSessionService.back(this.diagnosisSessionId).subscribe({
        next: (diagnosisSession) => {
          this.diagnosisSession = diagnosisSession;
          this.diagnosisSessionStatus = diagnosisSession.diagnosisStatusName;
        },
        error: (err: Error) => this.openBlockingWarning(err.message),
      });
    }
  }

  gotoPage(status: string) {
    const { queryParams } = this.#activatedRoute.snapshot;
    let nextPageUrl = this.externalId ? PARAMS.ExternalId : '';
    switch (status) {
      case CURRENT_STATUS.IN_PROGRESS:
        if (this.currentStep() === ROUTES.VIEW_SUMMARY)
          nextPageUrl = ROUTES.VIEW_SUMMARY;
        else nextPageUrl = ROUTES.SELECT_SOLUTIONS;
        break;
      case CURRENT_STATUS.DIAGNOSIS_COMPLETED:
        nextPageUrl = ROUTES.VIEW_SUMMARY;
        break;
      case CURRENT_STATUS.SOLUTION_SELECTED:
        nextPageUrl = ROUTES.SOLUTION_SELECTION;
        break;
    }
    this.currentStep.set(nextPageUrl);
    this.#router.navigate(
      [
        `${this.isVDR ? ROUTES.SELECT_SOLUTIONS : nextPageUrl}/${this.externalId || this.diagnosisSessionId}`,
      ],
      { queryParams },
    );
  }

  dispatchScreenState(): void {
    this.#globalStore.dispatch(
      setScreenStates({
        isInitializeDiagnosisScreen:
          this.currentStep() === ROUTES.SELECT_CHALLENGES,
      }),
    );
  }

  dispatchIsSelectSolutionFlag(options: { isEnable: boolean }) {
    this.#globalStore.dispatch(
      setIsSelectSolutionFlag({
        isIsSelectSolutionFlag: options.isEnable,
      }),
    );
  }

  restartWorkflow(): void {
    this.dialogCommonService
      .confirm({
        type: 'warning',
        title: 'New Workflow Confirmation',
        message: `Restarting the flow will remove all previously entered or selected data.`,
        cancelLabel: 'Cancel',
        confirmLabel: 'New Workflow',
      })
      .pipe(
        filter((res: boolean) => res),
        switchMap(() =>
          this.#diagnosisSessionService.restartSession(this.diagnosisSessionId),
        ),
      )
      .subscribe(() => {
        this.resetWorkflowState();
        this.challengeStore.clearStore();
      });
  }

  resetWorkflowState() {
    this.initializeWorkflowStates();
    this.resetDiagnosisStatus();
    this.handleRouterNavigate();
  }

  dispatchIsDesignApproved(): void {
    this.#globalStore.dispatch(
      setIsDesignApproved({
        isDesignApproved: true,
      }),
    );
  }

  initializeWorkflowStates(): void {
    this.#globalStore.dispatch(
      setChallenges({
        pairChallenges: [],
      }),
    );

    this.#globalStore.dispatch(
      setIsAvailableSolutionSelected({
        isSelectedSolutions: false,
      }),
    );

    this.#globalStore.dispatch(
      setAvailableSolutions({
        availableSolutions: [],
      }),
    );
  }

  initializeSession() {
    const subChallengeIds = this.challengeStore
      .pairChallenges()
      .map((item) => item.subChallengeId!);

    this.#diagnosisSessionService
      .initializeSession({
        subChallengeId: subChallengeIds[0] || '',
        pslCode: this.challengeStore.selectedPSL().code || '',
      })
      .subscribe((diagnosisSession) => {
        this.diagnosisSessionId = diagnosisSession?.id;
        this.diagnosisSession = diagnosisSession;
        this.diagnosisSessionStatus = diagnosisSession.diagnosisStatus.name;
        this.currentStep.set(ROUTES.SELECT_SOLUTIONS);
        this.handleRouterNavigate();
        this.dispatchScreenState();
      });
  }

  solutionSelectionCompleted() {
    this.#diagnosisSessionService
      .completeOneOffSession(this.diagnosisSessionId)
      .subscribe((diagnosisSession) => {
        this.diagnosisSessionId = diagnosisSession?.id;
        this.diagnosisSession = diagnosisSession;
        this.diagnosisSessionStatus = diagnosisSession.diagnosisStatus.name;
        this.currentStep.set(ROUTES.SOLUTION_SELECTION);
        this.availableSolutionStore.disabledAllSolution(
          this.diagnosisSessionStatus === CURRENT_STATUS.SOLUTION_SELECTED ||
            this.diagnosisSessionStatus === CURRENT_STATUS.APPROVED_FOR_DESIGN,
        );
        this.toastr.add({
          severity: 'success',
          summary: 'Solution Selection Completed',
          detail: 'Please return to the VDR to continue with the process.',
          closable: true,
          life: 5000,
        });
        this.dispatchScreenState();
      });
  }

  openPSLSelection() {
    this.dialogCommonService.openPSLSelection().subscribe((psl: PSL) => {
      if (this.challengeStore.selectedPSL().id !== psl.id) {
        localStorage.setItem(
          LOCAL_VARS.SelectedPSL,
          JSON.stringify({ name: psl.name, id: psl.id }),
        );
        this.challengeStore.clearStore();
        this.challengeStore.setSelectedPSL(psl);
        this.challengeStore.loadChallengeByPSL(psl.id);
      }
    });
  }

  openUserSetting() {
    this.dialogCommonService.openUserSetting().subscribe();
  }

  openBlockingWarning(message: string) {
    this.dialogCommonService.show(true, {
      title: 'Blocking session',
      message,
      closeLabel: 'Close',
      closable: false,
    });
  }
}
